/**
 * RootRedirect — handles the `/` route by redirecting based on auth state.
 *
 * Uses useAuth().isLoggedIn — NOT the httpOnly cookie resolveRootRedirectTarget function.
 * The `spk_sid` cookie is httpOnly so document.cookie cannot read it; the cookie-based
 * function would always return LOGIN_PATH for logged-in users, bouncing them to login
 * on every hard-refresh of `/`.
 *
 * Impersonation bypass: spk_impersonation=1 is intentionally NOT httpOnly (set by
 * login-page.tsx for admin handoff). It skips the loading spinner to avoid the flash
 * but grants no API privilege — security relies on per-request validation.
 */

import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/use-auth";
import { IMPERSONATION_COOKIE_NAME, getBrowserCookieValue } from "@/lib/auth/cookies";

export function RootRedirect() {
  const navigate = useNavigate();
  const { isLoggedIn, isLoading } = useAuth();

  // spk_impersonation is UX-ONLY: set non-httpOnly by login-page.tsx for admin token handoff.
  // It lets us skip the loading spinner for impersonation flows; it never grants API access.
  const isImpersonating = getBrowserCookieValue(IMPERSONATION_COOKIE_NAME) === "1";

  useEffect(() => {
    if (isImpersonating) {
      navigate("/home", { replace: true });
      return;
    }
    if (!isLoading) {
      navigate(isLoggedIn ? "/home" : "/auth/login", { replace: true });
    }
  }, [isLoading, isLoggedIn, isImpersonating, navigate]);

  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
    </div>
  );
}
